import { useState, useEffect, RefObject } from 'react';

export interface UseInViewportOptions {
  /**
   * Margin around the viewport's bounding box. Accepts CSS margin values (px or %).
   * Use this for pixel-based proximity triggers—e.g., '300px 0px' fires when the
   * element is within 300px of the viewport edge.
   */
  rootMargin?: string;

  /**
   * Observe only once: stop observing after the first time the element becomes visible.
   */
  onlyOnce?: boolean;
}

/**
 * Hook to determine if an element is in (or near) the browser viewport.
 * Uses Intersection Observer API for optimal performance in a single-page app.
 *
 * @param ref - MutableRef object pointing to the target element.
 * @param options - Settings:
 *   - `rootMargin`: pixel/percent offsets for proximity
 *   - `onlyOnce`: automatically unobserve after first intersection
 * @returns Whether the element is currently within (or near) the viewport.
 *
 */
export function useInView<T extends Element = Element>(
  ref: RefObject<T>,
  options: UseInViewportOptions = {},
) {
  const { rootMargin = '0px', onlyOnce = false } = options;

  const [inViewport, setInViewport] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // SSR-safe: assume visible if no IntersectionObserver
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setInViewport(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;
        setInViewport(isVisible);

        if (isVisible && onlyOnce) {
          observer.unobserve(entry.target);
        }
      },
      { rootMargin },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [ref, rootMargin, onlyOnce]);

  return inViewport;
}
